/**
 * @fileoverview Shared text input atom.
 * @description The one styled text input used across every form field. Visual variation (error
 * state, room for a trailing icon button, one of two height/radius treatments) is selected via
 * props rather than an overridable className, so two utility classes never compete to set the
 * same CSS property.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  invalid?: boolean
  hasTrailingAddon?: boolean
  size?: 'lg' | 'md'
}

const SIZE_CLASS_NAMES: Record<NonNullable<InputProps['size']>, string> = {
  lg: 'h-[52px] rounded-[9px]',
  md: 'h-12 rounded-[8px]',
}

/**
 * @component
 * @param {InputProps} props - Standard input attributes plus the style switches below.
 * @param {boolean} [props.invalid] - Shows the error border/focus ring when true.
 * @param {boolean} [props.hasTrailingAddon] - Reserves right-side padding for a trailing icon
 * button (e.g. the password show/hide toggle).
 * @param {'lg' | 'md'} [props.size] - "lg" (default, 52px/9px radius) or "md" (48px/8px radius).
 */
function Input({ invalid = false, hasTrailingAddon = false, size = 'lg', ...props }: InputProps) {
  const borderClassName = invalid
    ? 'border-red-400 focus:ring-red-200'
    : 'border-input-border focus:ring-primary/20'
  const paddingClassName = hasTrailingAddon ? 'pr-[52px]' : 'pr-4'

  return (
    <input
      className={`w-full border bg-input-bg pl-4 text-[16px] text-label placeholder:text-placeholder focus:outline-none focus:ring-2 ${SIZE_CLASS_NAMES[size]} ${borderClassName} ${paddingClassName}`}
      {...props}
    />
  )
}

export default Input
